import { useMemo, useState, useRef, useEffect } from 'react'

import { selectedProductStore, cartOpenStore, useStore, emitCartFlux, relockPointer, lightboxStore, registerBubbleEl, productSpinPad } from '../../store/uiStore'
import useCart, { formatPrice } from '../../hooks/useCart'
import { IS_TOUCH } from '../../config/touch'

// ============================================================
// Pagina prodotto: scheda specifiche a DESTRA; a sinistra resta
// visibile il mondo 3D, dove ProductShowcase porta la scatola
// vera del prodotto davanti alla camera. Trascinando il mouse
// sull'area libera si ruota la scatola (scrive in productSpin).
// Selettori opzioni + quantità + "Aggiungi al carrello".
// ============================================================

// Colori per gli swatch della nuvoletta: nomi italiani → CSS;
// per il resto si prova il nome come colore CSS (inglese incluso),
// altrimenti si ripiega sulla chip col testo
const COLORI = {
  rosso: '#e53935', blu: '#1e88e5', verde: '#43a047', nero: '#151515',
  bianco: '#ffffff', giallo: '#fdd835', rosa: '#ec6fa8', viola: '#8e24aa',
  arancione: '#fb8c00', grigio: '#9e9e9e', marrone: '#795548',
  beige: '#d7c4a3', azzurro: '#64b5f6', celeste: '#8fd8ff',
  oro: '#d4af37', argento: '#c0c0c0',
}
function coloreCss(value) {
  const v = value.toLowerCase().trim()
  if (COLORI[v]) return COLORI[v]
  const s = new Option().style
  s.color = v
  return s.color ? v : null
}
const isColorOption = (name) => /col(o|ou)?r/i.test(name)

// Prezzo della variante: se c'è uno sconto automatico (dalla API
// get-discounts) mostra il prezzo SCONTATO + originale barrato, con
// la nota che lo sconto viene applicato al pagamento — altrimenti il
// cliente vedrebbe -20% sullo scaffale e prezzo pieno in scheda.
function PriceBlock({ variant, discount }) {
  if (!variant) return <div className="pp-price">—</div>

  let discounted = null
  if (discount) {
    const amount = Number(variant.price.amount)
    if (discount.percentage) discounted = amount * (1 - discount.percentage)
    else if (discount.amountOff) discounted = Math.max(0, amount - discount.amountOff)
  }

  if (discounted !== null) {
    return (
      <>
        <div className="pp-price">
          <span className="pp-price-discounted">
            {formatPrice({ amount: discounted, currencyCode: variant.price.currencyCode })}
          </span>
          <span className="pp-compare">{formatPrice(variant.price)}</span>
          <span className="pp-discount-pill">{discount.valueText}</span>
        </div>
        <div className="pp-discount-note">Sconto applicato automaticamente al pagamento</div>
      </>
    )
  }

  return (
    <div className="pp-price">
      {formatPrice(variant.price)}
      {variant.compareAtPrice &&
        Number(variant.compareAtPrice.amount) > Number(variant.price.amount) && (
          <span className="pp-compare">{formatPrice(variant.compareAtPrice)}</span>
      )}
    </div>
  )
}

export default function ProductPage() {
  const product = useStore(selectedProductStore)
  if (!product) return null
  // key = product.id → lo stato interno si resetta cambiando prodotto
  return <ProductPageInner key={product.id} product={product} />
}

function ProductPageInner({ product }) {
  const { addLine, adding } = useCart()

  const variants = useMemo(
    () => product.variants.edges.map((e) => e.node),
    [product]
  )

  // Galleria: immagini + video del prodotto (media Shopify).
  // Per i video si preferisce l'mp4 720p (compatibile ovunque,
  // peso ragionevole); l'm3u8 non va su Chrome.
  const media = useMemo(() => {
    const imgs = product.images.edges.map((e) => ({ type: 'image', url: e.node.url }))
    const vids = (product.media?.edges || [])
      .filter((e) => e.node.mediaContentType === 'VIDEO')
      .map((e) => {
        const mp4s = (e.node.sources || []).filter((s) => s.mimeType === 'video/mp4')
        const src = mp4s.find((s) => s.url.includes('720p')) || mp4s[0]
        return { type: 'video', url: src?.url, poster: e.node.previewImage?.url }
      })
      .filter((v) => v.url)
    return [...imgs, ...vids]
  }, [product])

  // Indice del media aperto a schermo intero (null = chiusa). Store
  // condiviso: si apre anche cliccando le card del carosello 3D.
  const lightbox = useStore(lightboxStore)
  const setLightbox = (v) => lightboxStore.set(v)
  const lightboxOpen = lightbox !== null && !!media[lightbox]

  // reset all'apertura di un nuovo prodotto
  useEffect(() => { lightboxStore.set(null) }, [])

  // se la lightbox si apre mentre si è in pointer lock (click su una
  // card mirandola), libera il mouse: servono ✕ e frecce cliccabili
  useEffect(() => {
    if (lightboxOpen && document.pointerLockElement) document.exitPointerLock()
  }, [lightboxOpen])

  // Tastiera nella lightbox: frecce = scorri, Esc = chiudi SOLO la
  // lightbox (in capture + stopPropagation, così l'Esc non arriva
  // all'handler globale di Hud che chiuderebbe anche la scheda)
  useEffect(() => {
    if (!lightboxOpen) return
    const onKey = (e) => {
      if (e.code === 'Escape') {
        e.stopPropagation()
        setLightbox(null)
      }
      // stopPropagation anche sulle frecce: sono mappate su sinistra/destra
      // nei KeyboardControls e muoverebbero il personaggio mentre sfogli
      if (e.code === 'ArrowRight') {
        e.stopPropagation()
        e.preventDefault()
        lightboxStore.set((i) => (i + 1) % media.length)
      }
      if (e.code === 'ArrowLeft') {
        e.stopPropagation()
        e.preventDefault()
        lightboxStore.set((i) => (i - 1 + media.length) % media.length)
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [lightboxOpen, media.length])

  // Shopify usa un'opzione fittizia "Title: Default Title" quando
  // il prodotto non ha varianti reali → in quel caso niente selettori
  const hasRealOptions = !(
    product.options.length === 1 &&
    product.options[0].values.length === 1 &&
    product.options[0].values[0] === 'Default Title'
  )

  // Selezione iniziale: la prima variante disponibile (o la prima in assoluto)
  const initialVariant = variants.find((v) => v.availableForSale) || variants[0]
  const [selection, setSelection] = useState(() => {
    const sel = {}
    initialVariant?.selectedOptions.forEach((o) => { sel[o.name] = o.value })
    return sel
  })
  const [quantity, setQuantity] = useState(1)
  const [feedback, setFeedback] = useState(null) // 'ok' | 'error' | null
  // Nuvoletta ridotta a icona: resta solo titolo + prezzo (i tasti
  // Invio/frecce/rotella continuano a funzionare anche da ridotta)
  const [minimized, setMinimized] = useState(false)
  // Quale opzione colore ha i pallini aperti. Ne sta aperta UNA sola: i
  // colori partono chiusi dietro un pallino multicolore e si aprono
  // cliccandolo (vedi il blocco piu' sotto).
  const [colorAperto, setColorAperto] = useState(null)
  // Quali opzioni colore l'utente ha scelto DAVVERO. Serve perche' Shopify
  // preseleziona sempre una variante: senza questo il pallino partirebbe gia'
  // colorato e il multicolore non si vedrebbe mai. Finche' non ha scelto, il
  // pallino e' multicolore e accanto c'e' il NOME del colore corrente — se no
  // si rischia di aggiungere al carrello un colore che il pallino non mostrava.
  const [colorScelto, setColorScelto] = useState({})
  // L'opzione colore: non sta piu' fra le righe delle opzioni ma nella riga
  // dei comandi, accanto a quantita' e carrello.
  const opzioneColore = useMemo(
    () => (product.options || []).find((o) => isColorOption(o.name)) || null,
    [product]
  )

  // Variante corrispondente alla combinazione di opzioni scelta
  const currentVariant = variants.find((v) =>
    v.selectedOptions.every((o) => selection[o.name] === o.value)
  )

  // Una combinazione è acquistabile se esiste una variante disponibile
  const valueIsAvailable = (optionName, value) =>
    variants.some(
      (v) =>
        v.availableForSale &&
        v.selectedOptions.every((o) =>
          o.name === optionName ? o.value === value : selection[o.name] === o.value
        )
    )

  const selectOption = (name, value) => {
    setSelection((prev) => ({ ...prev, [name]: value }))
    setFeedback(null)
  }

  // --- Mini-joystick di rotazione (pp-spin-pad) ---
  // Trascini il pomello → il prodotto ruota con velocità proporzionale
  // allo spostamento; rilasci → si ferma. Scrive productSpinPad (x/y
  // in -1..1), che ProductShowcase applica a ogni frame.
  const padRef = useRef(null)
  const knobRef = useRef(null)
  const padActive = useRef(false)
  const PAD_R = 16
  const padMove = (e) => {
    if (!padActive.current || !padRef.current) return
    const rect = padRef.current.getBoundingClientRect()
    let dx = e.clientX - (rect.left + rect.width / 2)
    let dy = e.clientY - (rect.top + rect.height / 2)
    const m = Math.hypot(dx, dy)
    if (m > PAD_R) {
      dx *= PAD_R / m
      dy *= PAD_R / m
    }
    productSpinPad.x = dx / PAD_R
    productSpinPad.y = dy / PAD_R
    if (knobRef.current) knobRef.current.style.transform = `translate(${dx}px, ${dy}px)`
  }
  const padDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId)
    padActive.current = true
    padMove(e)
  }
  const padUp = () => {
    padActive.current = false
    productSpinPad.x = 0
    productSpinPad.y = 0
    if (knobRef.current) knobRef.current.style.transform = ''
  }

  // --- Comandi da tastiera col prodotto in mano (pointer locked) ---
  // Invio = aggiungi · ←→ = cicla varianti · ↑↓/rotella = quantità.
  // In CAPTURE con stopPropagation: le frecce sono mappate anche sul
  // movimento del player e NON devono farlo camminare.
  useEffect(() => {
    const cicla = (dir) => {
      const disponibili = variants.filter((v) => v.availableForSale)
      if (disponibili.length === 0) return
      const idx = disponibili.findIndex((v) => v.id === currentVariant?.id)
      const next = disponibili[(idx + dir + disponibili.length) % disponibili.length]
      const sel = {}
      next.selectedOptions.forEach((o) => { sel[o.name] = o.value })
      setSelection(sel)
      setFeedback(null)
    }
    const onKey = (e) => {
      if (lightboxStore.get() !== null) return // lì le frecce sfogliano i media
      if (e.code === 'Enter') {
        e.stopPropagation()
        handleAddToCart()
      } else if (e.code === 'ArrowUp') {
        e.stopPropagation(); e.preventDefault()
        setQuantity((q) => q + 1)
      } else if (e.code === 'ArrowDown') {
        e.stopPropagation(); e.preventDefault()
        setQuantity((q) => Math.max(1, q - 1))
      } else if (e.code === 'ArrowRight') {
        e.stopPropagation(); e.preventDefault()
        cicla(1)
      } else if (e.code === 'ArrowLeft') {
        e.stopPropagation(); e.preventDefault()
        cicla(-1)
      }
    }
    const onWheel = (e) => {
      if (lightboxStore.get() !== null) return
      e.preventDefault()
      setQuantity((q) => Math.max(1, q + (e.deltaY < 0 ? 1 : -1)))
    }
    window.addEventListener('keydown', onKey, true)
    window.addEventListener('wheel', onWheel, { passive: false })
    return () => {
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('wheel', onWheel)
    }
  })

  const close = () => {
    lightboxStore.set(null)
    selectedProductStore.set(null)
    relockPointer() // il click su ✕/backdrop è il gesto che permette il ri-lock
  }

  const handleAddToCart = async () => {
    if (!currentVariant) return
    try {
      await addLine(currentVariant.id, quantity)
      emitCartFlux(product.id) // flusso celeste prodotto → cassa nella scena 3D
      // Chiudi tutta la UI: il flusso si vede in pieno nella scena
      lightboxStore.set(null)
      cartOpenStore.set(false)
      selectedProductStore.set(null)
      relockPointer()
    } catch (err) {
      console.error('Errore aggiunta al carrello:', err)
      setFeedback('error')
    }
  }

  return (
    // Backdrop trasparente ai click: sotto ci sono canvas e carosello 3D
    <div className="pp-backdrop">
      {/* Nuvoletta compatta che sorvola il prodotto in vetrina:
          ProductShowcase la posiziona ogni frame (registerBubbleEl) */}
      {/* INVOLUCRO: la scheda piu' la COLONNA DI LATO. E' questo che
          ProductShowcase muove a ogni frame (registerBubbleEl), non piu' la
          sola scheda, o la colonna resterebbe indietro. */}
      <div className="pp-bubble-wrap" ref={(el) => registerBubbleEl(el)}>
      <div className={minimized ? 'pp-bubble min' : 'pp-bubble'}>
        <button className="pp-close pp-bubble-close" onClick={close} aria-label="Chiudi">✕</button>
        <button
          className="pp-close pp-bubble-minbtn"
          onClick={() => setMinimized((m) => !m)}
          title={minimized ? 'Espandi' : 'Riduci'}
          aria-label={minimized ? 'Espandi la scheda' : 'Riduci la scheda'}
        >
          {minimized ? '+' : '–'}
        </button>

        <div className="pp-bubble-title">{product.title}</div>
        <PriceBlock variant={currentVariant} discount={product.activeDiscount} />

        {/* FOTO E VIDEO, ED E' QUI CHE DEVONO STARE.
            La lightbox e la lista dei media c'erano gia' ed erano complete, ma
            l'unico modo di aprirle erano le card del carosello 3D — che pero'
            orbitano attorno al prodotto e si rimpiccioliscono con lui: da
            quando il prodotto sta in mano (scala da 1.1 a 0.28) erano diventate
            un quarto, cioe' invisibili. Segnalato: «non ho un modo per
            visualizzare i file multimediali collegati». Le miniature nella
            scheda sono il posto dove uno le cerca. */}
        {!minimized && media.length > 0 && (
          <div className="pp-bubble-media">
            <div className="pp-thumbs">
              {media.map((m, i) => (
                <button
                  key={`${m.url}-${i}`}
                  className={'pp-thumb' + (m.type === 'video' ? ' video' : '')}
                  onClick={() => setLightbox(i)}
                  title={m.type === 'video' ? 'Apri il video' : 'Apri la foto'}
                  aria-label={m.type === 'video' ? `Apri il video ${i + 1}` : `Apri la foto ${i + 1}`}
                >
                  {(m.type === 'image' || m.poster) && (
                    <img src={m.type === 'video' ? m.poster : m.url} alt="" draggable="false" />
                  )}
                  {m.type === 'video' && <span className="pp-thumb-play">▶</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {!minimized && product.description && <p className="pp-bubble-desc">{product.description}</p>}

        {/* Le opzioni NON colore restano righe di chip. Il colore no: sta
            dietro un pallino solo, in fondo, nella riga dei comandi. Una fila
            di sei pallini occupava una riga intera per una scelta che si fa
            una volta. */}
        {!minimized && hasRealOptions && product.options.map((option) => {
          if (!isColorOption(option.name)) {
            return (
              <div className="pp-bubble-row" key={option.name}>
                <span className="pp-bubble-optname">{option.name}</span>
                <div className="pp-bubble-values">
                  {option.values.map((value) => {
                    const active = selection[option.name] === value
                    const available = valueIsAvailable(option.name, value)
                    return (
                      <button
                        key={value}
                        className={'pp-option-btn' + (active ? ' active' : '') + (available ? '' : ' soldout')}
                        onClick={() => selectOption(option.name, value)}
                      >
                        {value}
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          }
          return null   // i colori stanno nella riga dei comandi, qui sotto
        })}

        {/* LA RIGA DEI COMANDI: colore, quantita', carrello. Chiesto di
            rimettere il numero e il carrello IN ORIZZONTALE accanto ai colori;
            di lato alla finestra resta il solo joystick. */}
        {!minimized && (
        <div className="pp-bubble-azioni">
          {opzioneColore && (() => {
            const option = opzioneColore
            const scelto = selection[option.name]
            const daUtente = colorScelto[option.name]
            const cssScelto = daUtente ? coloreCss(scelto) : null
            const aperto = colorAperto === option.name
            return (
              <div className={'pp-colori' + (aperto ? ' aperto' : '')}>
                <button
                  className={'pp-swatch pp-swatch-capo' + (cssScelto ? '' : ' multi')}
                  style={cssScelto ? { background: cssScelto } : undefined}
                  title={scelto ? `Colore: ${scelto}` : 'Scegli il colore'}
                  aria-label={scelto ? `Colore ${scelto}, cambia` : 'Scegli il colore'}
                  aria-expanded={aperto}
                  onClick={() => setColorAperto(aperto ? null : option.name)}
                />
                {/* LA RUOTA: un cerchio che si apre dalla palla, coi colori
                    disposti dentro in tondo. I pallini partono tutti dal
                    centro (traslazione zero) e volano al loro posto uno dopo
                    l'altro: e' la stessa animazione al contrario quando si
                    richiude. L'angolo parte dall'alto e gira in senso orario. */}
                <div className="pp-colori-ruota">
                  {option.values.map((value, i) => {
                    const active = scelto === value
                    const available = valueIsAvailable(option.name, value)
                    const css = coloreCss(value)
                    const ang = (-90 + (360 / option.values.length) * i) * Math.PI / 180
                    const R = option.values.length > 6 ? 50 : 42
                    return (
                      <button
                        key={value}
                        title={value + (available ? '' : ' — esaurito')}
                        aria-label={value}
                        tabIndex={aperto ? 0 : -1}
                        className={'pp-swatch' + (active ? ' active' : '') + (available ? '' : ' soldout')}
                        style={{
                          background: css || '#888',
                          '--x': `${(Math.cos(ang) * R).toFixed(1)}px`,
                          '--y': `${(Math.sin(ang) * R).toFixed(1)}px`,
                          transitionDelay: `${i * 28}ms`,
                        }}
                        onClick={() => {
                          selectOption(option.name, value)
                          setColorScelto((c) => ({ ...c, [option.name]: true }))
                          setColorAperto(null)
                        }}
                      />
                    )
                  })}
                </div>
              </div>
            )
          })()}

          <div className="pp-qty">
            <button onClick={() => setQuantity((q) => Math.max(1, q - 1))} aria-label="Riduci la quantita">&#8722;</button>
            <span>{quantity}</span>
            <button onClick={() => setQuantity((q) => q + 1)} aria-label="Aumenta la quantita">+</button>
          </div>

          <button
            className={'pp-cart-btn' + (adding ? ' occupato' : '')}
            disabled={!currentVariant || !currentVariant.availableForSale || adding}
            onClick={handleAddToCart}
            title={adding ? 'Aggiungo...' : 'Aggiungi al carrello'}
            aria-label={adding ? 'Aggiungo al carrello' : 'Aggiungi al carrello'}
          >
            <svg viewBox="0 0 40 40" aria-hidden="true">
              <g className="pp-cart-freccia">
                <rect x="18.6" y="3" width="2.8" height="8.4" rx="1.4" />
                <path d="M15.2 10.2 L20 15.4 L24.8 10.2 Z" />
              </g>
              <g className="pp-cart-corpo" fill="none" strokeWidth="2.4"
                 strokeLinecap="round" strokeLinejoin="round">
                <path d="M4.4 13.2 H8.4 L11 26.4 H30.2" />
                <path d="M9.6 17.2 H33.6 L31.4 26.4 H11.2 Z" />
                <path d="M17.2 17.2 V26.4 M25 17.2 V26.4 M10.4 21.8 H32.5" />
              </g>
              <circle className="pp-cart-ruota" cx="14.4" cy="31.4" r="2.3" />
              <circle className="pp-cart-ruota" cx="27.6" cy="31.4" r="2.3" />
            </svg>
          </button>
        </div>
        )}

        {/* Lo stato che il bottone tondo non puo' piu' scrivere a parole:
            un'etichetta breve, e solo quando c'e' qualcosa da dire. */}
        {!minimized && (!currentVariant || !currentVariant.availableForSale) && (
          <div className="pp-stato">
            {!currentVariant ? 'Variante non disponibile' : 'Esaurito'}
          </div>
        )}
        {!minimized && feedback === 'error' && <div className="pp-feedback error">Errore: riprova</div>}

        {/* guida tasti: solo desktop (su touch si tappa direttamente) */}
        {!minimized && !IS_TOUCH && (
          <div className="pp-bubble-keys">
            <span><kbd>Invio</kbd> aggiungi</span>
            <span><kbd>←</kbd><kbd>→</kbd> variante</span>
            <span><kbd>rotella</kbd> quantità</span>
            <span><kbd>E</kbd> rimetti</span>
          </div>
        )}

        <div className="pp-bubble-tail" />
      </div>

      {/* DI LATO ALLA FINESTRA, IL SOLO JOYSTICK. Ci erano finiti anche
          quantita' e carrello, ma quei due sono tornati in orizzontale nella
          riga dei comandi: girare il prodotto e' un gesto continuo, e sta
          meglio staccato dai due comandi che si premono e basta. Spunta con
          un'animazione quando la scheda si apre. */}
      {!minimized && (
        <div className="pp-rail">
          <div
            className="pp-spin-pad"
            ref={padRef}
            onPointerDown={padDown}
            onPointerMove={padMove}
            onPointerUp={padUp}
            onPointerCancel={padUp}
            title="Trascina per ruotare il prodotto"
          >
            <div className="pp-spin-knob" ref={knobRef}>&#8635;</div>
          </div>

        </div>
      )}
      </div>

      {/* Lightbox: foto/video a schermo intero, sopra la scheda */}
      {lightboxOpen && (
        <div className="pp-lightbox" onClick={(e) => { e.stopPropagation(); setLightbox(null) }}>
          <button className="pp-close pp-lightbox-close" aria-label="Chiudi">✕</button>

          {media.length > 1 && (
            <button
              className="pp-lightbox-nav prev"
              aria-label="Precedente"
              onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + media.length) % media.length) }}
            >‹</button>
          )}

          {media[lightbox].type === 'image' ? (
            <img src={media[lightbox].url} alt={product.title} onClick={(e) => e.stopPropagation()} />
          ) : (
            <video
              key={media[lightbox].url}
              src={media[lightbox].url}
              poster={media[lightbox].poster}
              controls
              autoPlay
              playsInline
              onClick={(e) => e.stopPropagation()}
            />
          )}

          {media.length > 1 && (
            <button
              className="pp-lightbox-nav next"
              aria-label="Successivo"
              onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % media.length) }}
            >›</button>
          )}

          {media.length > 1 && (
            <div className="pp-lightbox-count">{lightbox + 1} / {media.length}</div>
          )}
        </div>
      )}
    </div>
  )
}
